import { csrf_headers, get_token } from '@/router/index.js';

export const navbar_notification_data_methods = {
  build_notifications_url() {
    const url = new URL('/aic_one_api/notification_management/get_user_notifications/', process.env.VUE_APP_API_BASE_URL);
    url.searchParams.set('unread_only', 'false');
    url.searchParams.set('include_dismissed', 'false');
    return url.toString();
  },

  authed_headers(token, include_csrf = false) {
    return ({
      'Authorization': `Token ${token}`,
      'Content-Type': 'application/json',
      'X-Page-URL': window.location.href,
      ...(include_csrf ? csrf_headers(this.csrfToken) : {}),
    });
  },

  set_notifications_loading(show_loader, is_loading) {
    if (show_loader) {
      this.loading_notifications = is_loading;
    }
  },

  async fetch_notifications(show_loader = true) {
    const token = get_token();
    if (!this.ensure_notification_token(token)) return;

    this.set_notifications_loading(show_loader, true);

    try {
      const response = await fetch(this.build_notifications_url(), {
        method: 'GET',
        credentials: 'include',
        headers: this.authed_headers(token),
      });

      await this.handle_notifications_response(response);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      this.set_notifications_loading(show_loader, false);
    }
  },

  ensure_notification_token(token) {
    if (token) return true;

    this.$router.push({ name: 'Login' });
    return false;
  },

  is_unauthorized_response(response) {
    return [401, 403].includes(response.status);
  },

  get_notification_list(data) {
    return Array.isArray(data.notifications) ? data.notifications : [];
  },

  async handle_notifications_response(response) {
    if (this.handle_unauthorized_notifications(response)) return;
    if (!response.ok) return;

    const data = await response.json();
    if (data.status === 'success') {
      this.apply_fetched_notifications(this.get_notification_list(data));
    }
  },

  handle_unauthorized_notifications(response) {
    if (!this.is_unauthorized_response(response)) return false;

    this.$router.push({ name: 'Login' });
    return true;
  },

  apply_fetched_notifications(incoming) {
    if (this._notifications_initialized) {
      this.notify_new_notifications(incoming);
    }

    this.notifications = incoming;
    this._notifications_initialized = true;
  },

  notify_new_notifications(incoming) {
    const new_ones = this.unread_new_notifications(incoming);

    if (new_ones.length === 1) {
      this.notify_single_notification(new_ones[0]);
      return;
    }

    this.notify_notification_batch(new_ones);
  },

  unread_new_notifications(incoming) {
    const existing_ids = new Set(this.notifications.map(n => n.id));
    return incoming.filter(n => !existing_ids.has(n.id) && n.status !== 'READ');
  },

  notify_notification_batch(new_ones) {
    if (new_ones.length <= 1) return;

    this.fire_browser_notification(
      'New Notifications - AIC One',
      `You have ${new_ones.length} new notifications`,
      { tag: 'notif-batch' }
    );
  },

  notify_single_notification(notification) {
    this.fire_browser_notification(
      'New Notification - AIC One',
      notification.message,
      {
        tag: `notif-${notification.id}`,
        on_click: () => this.push_notification_url(notification.action_url),
      }
    );
  },

  apply_unread_count_response(data) {
    if (data.status === 'success') {
      this.unread_count = Number(data.unread_count ?? 0);
    }
  },

  async fetch_unread_count() {
    const token = get_token();
    if (!token) return;

    try {
      await this.request_unread_count(token);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  },

  async request_unread_count(token) {
    const response = await fetch(this.unread_count_url(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return;

    this.apply_unread_count_response(await response.json());
  },

  unread_count_url() {
    return `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/notification_management/get_unread_notification_count/`;
  },

  open_notifications() {
    this.fetch_notifications(false);
  },

  start_notification_polling() {
    this.notification_poll_interval = setInterval(() => {
      this.fetch_unread_count();
      this.fetch_notifications(false);
    }, 30000);
  },
};
